import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
    Save, Edit, CalendarDays, AlertCircle, CheckCircle2, Plus, Search, Trash2
} from 'lucide-react';

const AcademicYearManagement = () => {
    const [years, setYears] = useState([]);
    const [formData, setFormData] = useState({
        year_name: '',
        start_date: '',
        end_date: '',
        status: 'current'
    });
    const [isEditing, setIsEditing] = useState(false);
    const [editId, setEditId] = useState(null);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [loading, setLoading] = useState(false);

    const fetchYears = useCallback(async () => {
        try {
            setLoading(true);
            const res = await axios.get('/api/academic-years');
            setYears(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error('Fetch error:', err);
            showStatus('error', 'Failed to load academic years');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchYears();
    }, [fetchYears]);

    const showStatus = (type, text) => {
        setMessage({ type, text });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    };

    const validateForm = () => {
        const trimmedYearName = formData.year_name.trim();
        if (!trimmedYearName) {
            showStatus('error', 'Academic year name is required');
            return false;
        }
        if (!formData.start_date || !formData.end_date) {
            showStatus('error', 'Both start and end dates are required');
            return false;
        }
        const start = new Date(formData.start_date);
        const end = new Date(formData.end_date);
        if (isNaN(start) || isNaN(end)) {
            showStatus('error', 'Invalid date format');
            return false;
        }
        if (end < start) {
            showStatus('error', 'End date cannot be before start date');
            return false;
        }
        const duplicate = years.some((year) => {
            const sameName = (year.year_name || '').trim().toLowerCase() === trimmedYearName.toLowerCase();
            if (!sameName) return false;
            if (!isEditing) return true;
            return year.acd_id !== editId;
        });
        if (duplicate) {
            showStatus('error', `Academic year "${trimmedYearName}" already exists`);
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setLoading(true);
        try {
            const url = isEditing
                ? `/api/academic-years/${editId}`
                : '/api/academic-years';

            const method = isEditing ? 'put' : 'post';

            // LOGIC CHANGE: Removed manual acd_id generation.
            // The backend now generates IDs like ACD001, ACD002 etc. automatically.
            const dataToSubmit = {
                ...formData,
                year_name: formData.year_name.trim()
            };

            await axios[method](url, dataToSubmit);

            showStatus('success', isEditing ? 'Academic year updated' : 'Academic year created');

            setFormData({ year_name: '', start_date: '', end_date: '', status: 'current' });
            setIsEditing(false);
            setEditId(null);
            fetchYears();
        } catch (err) {
            console.error('Submit error:', err.response?.data || err.message);
            const errorMsg =
                err.response?.data?.error ||
                'Operation failed. Please ensure the backend supports the update route.';
            showStatus('error', errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (year) => {
        setFormData({
            year_name: year.year_name || '',
            start_date: year.start_date ? year.start_date.split('T')[0] : '',
            end_date: year.end_date ? year.end_date.split('T')[0] : '',
            status: year.status || 'current'
        });
        setIsEditing(true);
        setEditId(year.acd_id);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (acdId) => {
        if (!acdId) {
            showStatus('error', 'Cannot delete: ID missing');
            return;
        }
        if (!window.confirm('Delete this academic year? This cannot be undone.')) return;

        setLoading(true);
        try {
            await axios.delete(`/api/academic-years/${acdId}`);
            showStatus('success', 'Academic year deleted');
            fetchYears();
        } catch (err) {
            console.error('Delete error:', err.response?.data || err);
            const msg =
                err.response?.data?.error ||
                'Failed to delete (check if referenced by batches)';
            showStatus('error', msg);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setIsEditing(false);
        setEditId(null);
        setFormData({ year_name: '', start_date: '', end_date: '', status: 'current' });
    };

    const filteredYears = years.filter(
        (year) =>
            (year.year_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                year.status?.toLowerCase().includes(searchTerm.toLowerCase())) &&
            (filterStatus === 'all' || year.status === filterStatus)
    );

    const getStatusColor = (status) => {
        const colors = {
            current: { bg: '#dcfce7', text: '#166534', label: 'Current' },
            upcoming: { bg: '#e0f2fe', text: '#1e40af', label: 'Upcoming' },
            past: { bg: '#f3f4f6', text: '#4b5563', label: 'Past' }
        };
        return colors[status] || { bg: '#f3f4f6', text: '#4b5563', label: status };
    };

    const styles = {
        container: {
            maxWidth: '1400px',
            margin: '0 auto',
            padding: '32px',
            background: '#f8fafc'
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '32px'
        },
        headerLeft: {
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
        },
        pageTitle: {
            fontSize: '28px',
            fontWeight: '700',
            color: '#1e293b',
            margin: 0
        },
        pageSubtitle: {
            fontSize: '16px',
            color: '#64748b',
            margin: 0
        },
        addButton: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 24px',
            background: 'linear-gradient(135deg, #1e3a8a, #0f766e)',
            color: '#fff',
            border: 'none',
            borderRadius: '12px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '15px'
        },
        formCard: {
            background: '#fff',
            borderRadius: '20px',
            padding: '32px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
            border: '1px solid #e2e8f0',
            marginBottom: '32px'
        },
        cardHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '28px',
            borderBottom: '1px solid #f1f5f9',
            paddingBottom: '20px'
        },
        cardTitle: {
            fontSize: '22px',
            fontWeight: '600',
            color: '#1e293b',
            margin: 0
        },
        formGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '24px',
            marginBottom: '28px'
        },
        inputGroup: {
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
        },
        label: {
            fontSize: '14px',
            fontWeight: '500',
            color: '#374151'
        },
        input: {
            padding: '14px 16px',
            border: '2px solid #e5e7eb',
            borderRadius: '12px',
            fontSize: '15px',
            outline: 'none'
        },
        saveBtn: {
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '14px 28px',
            background: 'linear-gradient(135deg, #1e3a8a, #0f766e)',
            color: '#fff',
            border: 'none',
            borderRadius: '12px',
            fontWeight: '600',
            cursor: 'pointer',
            fontSize: '15px'
        },
        cancelBtn: {
            padding: '10px 20px',
            background: '#f8fafc',
            color: '#64748b',
            border: '2px solid #e5e7eb',
            borderRadius: '12px',
            cursor: 'pointer',
            fontWeight: '500'
        },
        alert: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '16px 20px',
            borderRadius: '12px',
            marginBottom: '24px',
            borderLeft: '4px solid'
        },
        tableCard: {
            background: '#fff',
            borderRadius: '20px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
            border: '1px solid #e2e8f0',
            overflow: 'hidden'
        },
        tableCardHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '28px 32px',
            borderBottom: '1px solid #f1f5f9'
        },
        tableControls: {
            display: 'flex',
            gap: '20px'
        },
        searchContainer: {
            position: 'relative',
            width: '320px'
        },
        searchIcon: {
            position: 'absolute',
            left: '16px',
            top: '50%',
            transform: 'translateY(-50%)'
        },
        searchInput: {
            width: '100%',
            padding: '12px 16px 12px 48px',
            border: '2px solid #e5e7eb',
            borderRadius: '12px',
            outline: 'none',
            fontSize: '15px'
        },
        filterSelect: {
            width: '180px',
            padding: '12px 16px',
            border: '2px solid #e5e7eb',
            borderRadius: '12px',
            fontSize: '15px'
        },
        tableContainer: {
            overflowX: 'auto'
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse'
        },
        thead: {
            background: '#f8fafc'
        },
        th: {
            textAlign: 'left',
            padding: '20px 24px',
            color: '#64748b',
            fontSize: '13px',
            fontWeight: '600',
            borderBottom: '2px solid #f1f5f9'
        },
        tr: {
            borderBottom: '1px solid #f1f5f9'
        },
        td: {
            padding: '20px 24px',
            fontSize: '15px',
            color: '#1e293b'
        },
        statusBadge: {
            padding: '8px 16px',
            borderRadius: '25px',
            fontSize: '13px',
            fontWeight: '600'
        },
        actionButtons: {
            display: 'flex',
            gap: '12px'
        },
        editBtn: {
            padding: '10px',
            background: '#eff6ff',
            color: '#1e3a8a',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer'
        },
        deleteBtn: {
            padding: '10px',
            background: '#fef2f2',
            color: '#dc2626',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer'
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div style={styles.headerLeft}>
                    <CalendarDays size={28} color="#1e3a8a" />
                    <div>
                        <h1 style={styles.pageTitle}>Academic Year Management</h1>
                        <p style={styles.pageSubtitle}>Manage university academic calendars and sessions</p>
                    </div>
                </div>
                <button style={styles.addButton} onClick={handleCancel} disabled={loading}>
                    <Plus size={20} /> New Academic Year
                </button>
            </div>

            <div style={styles.formCard}>
                <div style={styles.cardHeader}>
                    <h3 style={styles.cardTitle}>
                        {isEditing ? 'Edit Academic Year' : 'Add New Academic Year'}
                    </h3>
                    {isEditing && (
                        <button style={styles.cancelBtn} onClick={handleCancel} disabled={loading}>
                            Cancel
                        </button>
                    )}
                </div>

                {message.text && (
                    <div
                        style={{
                            ...styles.alert,
                            backgroundColor: message.type === 'success' ? '#f0fdf4' : '#fef2f2',
                            color: message.type === 'success' ? '#166534' : '#991b1b',
                            borderLeftColor: message.type === 'success' ? '#22c55e' : '#ef4444'
                        }}
                    >
                        {message.type === 'success' ? <CheckCircle2 size={20} /> : <AlertCircle size={20} />}
                        <span style={{ marginLeft: '12px' }}>{message.text}</span>
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div style={styles.formGrid}>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>Academic Year Name *</label>
                            <input
                                type="text"
                                style={styles.input}
                                value={formData.year_name}
                                onChange={(e) => setFormData({ ...formData, year_name: e.target.value })}
                                required
                                placeholder="e.g. 2025-2026"
                            />
                        </div>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>Start Date *</label>
                            <input
                                type="date"
                                style={styles.input}
                                value={formData.start_date}
                                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                                required
                            />
                        </div>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>End Date *</label>
                            <input
                                type="date"
                                style={styles.input}
                                value={formData.end_date}
                                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                                required
                            />
                        </div>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>Status</label>
                            <select
                                style={styles.input}
                                value={formData.status}
                                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                            >
                                <option value="upcoming">Upcoming</option>
                                <option value="current">Current</option>
                                <option value="past">Past</option>
                            </select>
                        </div>
                    </div>

                    <button
                        type="submit"
                        style={{ ...styles.saveBtn, opacity: loading ? 0.7 : 1 }}
                        disabled={loading}
                    >
                        <Save size={18} />
                        {loading ? 'Processing...' : isEditing ? 'Update Academic Year' : 'Create Academic Year'}
                    </button>
                </form>
            </div>

            <div style={styles.tableCard}>
                <div style={styles.tableCardHeader}>
                    <h3 style={styles.cardTitle}>Academic Year List ({filteredYears.length})</h3>
                    <div style={styles.tableControls}>
                        <div style={styles.searchContainer}>
                            <Search size={18} color="#94a3b8" style={styles.searchIcon} />
                            <input
                                type="text"
                                placeholder="Search name or status..."
                                style={styles.searchInput}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <select
                            style={styles.filterSelect}
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                        >
                            <option value="all">All Status</option>
                            <option value="upcoming">Upcoming</option>
                            <option value="current">Current</option>
                            <option value="past">Past</option>
                        </select>
                    </div>
                </div>

                {loading && years.length === 0 ? (
                    <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                        Loading academic years...
                    </div>
                ) : (
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr style={styles.thead}>
                                    <th style={styles.th}>Academic Year</th>
                                    <th style={styles.th}>Duration</th>
                                    <th style={styles.th}>Status</th>
                                    <th style={styles.th}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredYears.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                                            No academic years found
                                        </td>
                                    </tr>
                                ) : (
                                    filteredYears.map((year) => {
                                        const statusInfo = getStatusColor(year.status);
                                        return (
                                            <tr key={year.acd_id} style={styles.tr}>
                                                <td style={styles.td}>{year.year_name}</td>
                                                <td style={styles.td}>
                                                    {year.start_date} → {year.end_date}
                                                </td>
                                                <td style={styles.td}>
                                                    <span
                                                        style={{
                                                            ...styles.statusBadge,
                                                            backgroundColor: statusInfo.bg,
                                                            color: statusInfo.text
                                                        }}
                                                    >
                                                        {statusInfo.label}
                                                    </span>
                                                </td>
                                                <td style={styles.td}>
                                                    <div style={styles.actionButtons}>
                                                        <button
                                                            onClick={() => handleEdit(year)}
                                                            style={styles.editBtn}
                                                            disabled={loading}
                                                            title="Edit"
                                                        >
                                                            <Edit size={16} />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(year.acd_id)}
                                                            style={styles.deleteBtn}
                                                            disabled={loading}
                                                            title="Delete"
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AcademicYearManagement;
